import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { TopNav } from "@/components/TopNav";
import { EVENT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/photos?limit=500`);
      setPhotos(r.data?.photos || []);
      setError(null);
    } catch (e) {
      setError("Photos didn't load. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const photoUrl = useCallback(
    (p) => `${process.env.REACT_APP_BACKEND_URL}${p.url}`,
    []
  );

  const closeLightbox = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNext = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [activeIndex, closeLightbox, showPrev, showNext]);

  const activePhoto = useMemo(
    () => (activeIndex === null ? null : photos[activeIndex]),
    [activeIndex, photos]
  );

  return (
    <main
      data-testid="gallery-page"
      className="relative bg-[#0a0a0a] text-[#FDFBF7] min-h-[100svh]"
    >
      <TopNav />

      {/* Header */}
      <header className="pt-32 md:pt-40 pb-10 md:pb-14 px-6 md:px-12 text-center">
        <div className="eyebrow-line justify-center inline-flex">The Night</div>
        <h1
          className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.05]"
          style={{ fontSize: "clamp(40px, 7vw, 88px)" }}
        >
          Saturday, June 27{" "}
          <span className="italic text-[#D4AF37]">in photos.</span>
        </h1>
        <p className="mt-6 text-[#A3A3A3] max-w-[54ch] mx-auto leading-[1.75]">
          Candlelight, walnut, records, and the room. Tap any image to view it
          full-screen. Thank you for being there.
        </p>
        <p className="mt-3 text-[#7a7a7a] text-xs tracking-[0.3em] uppercase">
          {EVENT.address} · {EVENT.city}
        </p>
      </header>

      {/* Body */}
      <section className="px-3 md:px-6 pb-24 md:pb-32">
        {loading && (
          <div className="text-center text-[#A3A3A3] py-24" data-testid="gallery-loading">
            Loading photos…
          </div>
        )}
        {error && (
          <div className="text-center text-red-400 py-24" data-testid="gallery-error">
            {error}
          </div>
        )}
        {!loading && !error && photos.length === 0 && (
          <div className="text-center py-24" data-testid="gallery-empty">
            <p className="font-serif-display text-[#FDFBF7] text-2xl md:text-3xl italic">
              The photos are being uploaded.
            </p>
            <p className="text-[#A3A3A3] mt-4">Check back soon.</p>
          </div>
        )}

        {photos.length > 0 && (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-2 max-w-[1400px] mx-auto"
            data-testid="gallery-grid"
          >
            {photos.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActiveIndex(idx)}
                data-testid={`gallery-thumb-${idx}`}
                className="group relative aspect-square overflow-hidden bg-[#111] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              >
                <img
                  src={photoUrl(p)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </button>
            ))}
          </div>
        )}

        {photos.length > 0 && (
          <p className="text-center text-[#7a7a7a] text-xs tracking-[0.3em] uppercase mt-12">
            {photos.length} photos
          </p>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {activePhoto && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-[#0a0a0a]/98 flex items-center justify-center"
            data-testid="gallery-lightbox"
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              data-testid="lightbox-close"
              className="absolute top-5 right-5 md:top-8 md:right-8 z-10 w-11 h-11 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0a] transition-colors flex items-center justify-center"
              aria-label="Close"
            >
              ✕
            </button>

            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showPrev();
                  }}
                  data-testid="lightbox-prev"
                  className="absolute left-3 md:left-8 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0a] transition-colors flex items-center justify-center text-xl"
                  aria-label="Previous"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showNext();
                  }}
                  data-testid="lightbox-next"
                  className="absolute right-3 md:right-8 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0a] transition-colors flex items-center justify-center text-xl"
                  aria-label="Next"
                >
                  →
                </button>
              </>
            )}

            <motion.img
              key={activePhoto.id}
              src={photoUrl(activePhoto)}
              alt=""
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[95vw] max-h-[85vh] object-contain shadow-2xl"
            />

            <div className="absolute bottom-4 md:bottom-6 left-0 right-0 text-center text-[#A3A3A3] text-xs tracking-[0.3em] uppercase pointer-events-none">
              {activeIndex + 1} / {photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
